import React, { useCallback, useEffect, useState } from "react";
import useFetch from "../../hooks/useFetch";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import logo from "../../assets/logo.png";

const MyDigitalCard = () => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();

  const userId = authState.user._id;

  const [profileData, setProfileData] = useState({
    userId: userId,
    name: authState.user.name || "",
    gender: 0,
    dob: "",
    email: authState.user.email || "",
    mobile: "",
    city: "",
    institution: "",
    socialLinks: {},
    personalPageLink: "",
    think5whysCode: authState.user.think5whyscode || "",
    image: "",
  });

  const [profileId, setProfileId] = useState("");
  const [createSuccessMsg, setCreateSuccessMsg] = useState("");

  const fetchUserProfile = useCallback(() => {
    try {
      const config = {
        url: "/myprofile/byid",
        method: "post",
        data: { userId },
        headers: { Authorization: authState.token },
      };
      fetchData(config)
        .then((response) => {
          if (response && response.status) {
            const { profile } = response;
            setProfileId(profile._id);
            setProfileData((prev) => ({
              ...prev,
              ...profile,
              socialLinks: profile.socialLinks || {},
            }));
          } else {
            setCreateSuccessMsg("Profile not found. Please retry.");
          }
        })
        .catch(() => setCreateSuccessMsg("Error loading profile."));
    } catch {
      setCreateSuccessMsg("Error loading profile.");
    }
  }, [userId, fetchData, authState.token]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

const safe = (v, fallback = "NA") => v && v.toString().trim() ? v : fallback;

const generateVCard = (profile) => {
  const fullName = safe(profile.name);
  const parts = fullName.split(" ");
  const firstName = parts[0] || "NA";
  const lastName = parts.slice(1).join(" ") || "NA";

  return `
    BEGIN:VCARD
    VERSION:3.0

    N:${lastName};${firstName};;;
    FN:${fullName}
    NICKNAME:${safe(profile.nickname)}

    ORG:${safe(profile.institution)};${safe(profile.department)};${safe(profile.office)}
    TITLE:${safe(profile.jobTitle)}

    TEL;TYPE=CELL:${safe(profile.mobile, "0000000000")}
    TEL;TYPE=HOME:${safe(profile.homePhone, "0000000000")}
    TEL;TYPE=WORK:${safe(profile.workPhone, "0000000000")}
    TEL;TYPE=FAX:${safe(profile.fax)}
    TEL;TYPE=PAGER:${safe(profile.pager)}

    EMAIL;TYPE=INTERNET:${safe(profile.email)}

    ADR;TYPE=HOME:;;${safe(profile.homeStreet)};${safe(profile.homeCity)};${safe(profile.homeState)};${safe(profile.homePostal)};${safe(profile.homeCountry)}
    ADR;TYPE=WORK:;;${safe(profile.workStreet)};${safe(profile.workCity)};${safe(profile.workState)};${safe(profile.workPostal)};${safe(profile.workCountry)}

    URL:${safe(profile.personalPageLink, "https://think5whys.com")}
    URL;TYPE=WORK:${safe(profile.workWebsite, "https://think5whys.com")}

    BDAY:${safe(profile.dob, "19000101")}

    NOTE:Think5Whys Digital ID ${safe(profile.think5whysCode)}

    X-GENDER:${safe(profile.gender, "U")}
    X-SPOUSE:${safe(profile.spouse)}
    X-CHILDREN:${safe(profile.children)}

    END:VCARD
    `.trim();
  };

  const handleSaveContact = () => {
    if (!profileData.mobile) {
      alert("Mobile number not available");
      return;
    }

    const vcard = generateVCard(profileData);
    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${profileData.name || "contact"}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="min-h-[100dvh] flex justify-center items-center bg-black py-8 px-4 flex-col">
        <div
          onClick={handleSaveContact}
          className="w-full max-w-xs sm:max-w-sm lg:max-w-md bg-black border-4 border-green-400 rounded-2xl 
                     shadow-xl text-blue-400 text-center p-5 flex flex-col items-center cursor-pointer
                    active:scale-95 transition"
        >
          {/* Profile Image */}
          <div className="flex justify-center mb-4">
            <img
              src={profileData.image || logo}
              alt="Profile"
              className="w-32 h-32 rounded-full border-2 border-gray-300 p-1 object-cover"
            />
          </div>

          {/* Profile Info */}
          <h2 className="text-yellow-300 font-bold text-lg sm:text-xl mb-1">
            {profileData.name}
          </h2>
          <p className="text-sm text-blue-200">ID#: {profileData.think5whysCode}</p>
          <p className="text-sm text-blue-200">
            Institution: {profileData.institution || "N/A"}
          </p>
          <p className="text-sm text-blue-200">{profileData.email}</p>
          <p className="text-sm text-blue-200">{profileData.mobile}</p>
          <p className="text-sm text-blue-200">{profileData.city}</p>

          {/* Social Links */}
          <div className="flex justify-center mt-5 space-x-5">
            {profileData.socialLinks?.facebook && (
              <a
                onClick={(e) => e.stopPropagation()}                
                href={profileData.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFacebook} size="lg" className="text-blue-500 hover:text-blue-400" />
              </a>
            )}
            {profileData.socialLinks?.instagram && (
              <a
                onClick={(e) => e.stopPropagation()}                
                href={profileData.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faInstagram} size="lg" className="text-pink-500 hover:text-pink-400" />
              </a>
            )}
            {profileData.socialLinks?.twitter && (
              <a
                onClick={(e) => e.stopPropagation()}              
                href={profileData.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faTwitter} size="lg" className="text-sky-400 hover:text-sky-300" />
              </a>
            )}
            {profileData.personalPageLink && (
              <a
                onClick={(e) => e.stopPropagation()}                
                href={profileData.personalPageLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faLinkedin} size="lg" className="text-blue-600 hover:text-blue-500" />
              </a>
            )}
          </div>

          {/* Footer */}
          <div className="w-full mt-6">
            <p className="flex items-center justify-center gap-2 text-xs bg-black border border-yellow-100 text-blue-700 py-2 rounded-md">
              Powered by
              <img
                src={logo}
                alt="Think5Whys"
                className="w-6 h-6 rounded-full"
              />
              think5whys.com
            </p>
          </div>

          {createSuccessMsg && (
            <p className="text-red-400 text-sm mt-3">{createSuccessMsg}</p>
          )}
        </div>
        <div><p className="text-xs text-green-300 mt-1 mb-2">Tap card to save contact 📇</p></div>
      </div>
    </div>
  );
};

export default MyDigitalCard;
